import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { mockMemorials } from '../data/mockData';
import { memorialThemes } from '../styles/themes';
import { useAuth } from '../context/AuthContext';
import { writeFetch, getSession } from '../api';
import { useMemorialOwner } from '../hooks/useMemorialOwner';

import TabStory from '../components/Memorial/TabStory';
import TabTributes from '../components/Memorial/TabTributes';
import TabGallery from '../components/Memorial/TabGallery';
import TabEvents from '../components/Memorial/TabEvents';
import TabLegacySettings from '../components/Memorial/TabLegacySettings';
import TabShowcase from '../components/Memorial/TabShowcase';
import TabFamilyTree from '../components/Memorial/TabFamilyTree';

import DonationProgressWidget from '../components/Memorial/DonationProgressWidget';
import ThemeSelector from '../components/Memorial/ThemeSelector';
import MemorialPlatformSettings from '../components/Memorial/MemorialPlatformSettings';
import PrivateAccessLock from '../components/Memorial/PrivateAccessLock';
import SoundscapePlayer from '../components/Memorial/SoundscapePlayer';
import MilestoneCountdown from '../components/Memorial/MilestoneCountdown';
import ShareModal from '../components/Modals/ShareModal';
import EstateManagementWidget from '../components/Memorial/EstateManagementWidget';
import TributeNotificationSettings from '../components/Memorial/TributeNotificationSettings';
import ClaimMemorialBanner from '../components/Memorial/ClaimMemorialBanner';
import { notifyMemorialFollowers } from '../hooks/useTributeNotifications';
import { nameToSlug } from '../services/memorialLinkService';

const {
  FiArrowLeft, FiHeart, FiShare2, FiBookOpen, FiImage,
  FiCalendar, FiShield, FiSettings, FiGrid, FiSliders, FiGitBranch,
} = FiIcons;

const CUSTOMIZATION_SUBTABS = [
  { id: 'theme',     label: 'Theme & Appearance',   icon: FiSettings },
  { id: 'platforms', label: 'Platform Preferences', icon: FiSliders  },
];

/**
 * Multi-strategy memorial resolution:
 *  1. ?id= query param  (slug URL embeds the real id as a query param)
 *  2. Exact id match    (legacy direct links like /#/memorial/m1)
 *  3. Slug match        (name-based URL like /#/memorial/eleanor-grace-thompson)
 *  4. Fallback to first memorial
 */
function resolveMemorial(param, search) {
  const qs  = new URLSearchParams(search);
  const qId = qs.get('id');

  if (qId) {
    const byQId = mockMemorials.find(m => m.id === qId);
    if (byQId) return byQId;
  }

  const byId = mockMemorials.find(m => m.id === param);
  if (byId) return byId;

  const bySlug = mockMemorials.find(m => nameToSlug(m.name) === param);
  if (bySlug) return bySlug;

  return mockMemorials[0];
}

const MemorialDetail = () => {
  const { id }   = useParams();
  const location = useLocation();
  const { user } = useAuth();

  const memorial  = resolveMemorial(id, location.search);
  const {
    isOwner: isCreator, signedIn, claim, claiming, claimError,
  } = useMemorialOwner(memorial);

  const [activeTab, setActiveTab]       = useState('story');
  const [customSubTab, setCustomSubTab] = useState('theme');
  const [isLiked, setIsLiked]           = useState(false);
  const [candleCount, setCandleCount]   = useState(124);
  const [isUnlocked, setIsUnlocked]     = useState(!memorial.isPrivate);
  const [shareOpen, setShareOpen]       = useState(false);
  
  const [coverImage, setCoverImage] = useState(() => {
    try {
      return localStorage.getItem(`memorial_cover_${memorial.id}`) || memorial?.coverImage;
    } catch { return memorial?.coverImage; }
  });
  
  const [profileImage, setProfileImage] = useState(() => {
    try {
      return localStorage.getItem(`memorial_profile_${memorial.id}`) || memorial?.profileImage;
    } catch { return memorial?.profileImage; }
  });

  const [currentTheme, setCurrentTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(`memorial_theme_${memorial.id}`);
      return saved ? JSON.parse(saved) : memorialThemes.heritage;
    } catch { return memorialThemes.heritage; }
  });

  const handleSaveTheme = (t) => {
    try { 
      localStorage.setItem(`memorial_theme_${memorial.id}`, JSON.stringify(t)); 
      localStorage.setItem(`memorial_cover_${memorial.id}`, coverImage);
      localStorage.setItem(`memorial_profile_${memorial.id}`, profileImage);
    } catch {}
    setCurrentTheme(t);
  };

  useEffect(() => {
    if (memorial) {
      setCoverImage(prev => localStorage.getItem(`memorial_cover_${memorial.id}`) || memorial.coverImage);
      setProfileImage(prev => localStorage.getItem(`memorial_profile_${memorial.id}`) || memorial.profileImage);
      
      const savedTheme = localStorage.getItem(`memorial_theme_${memorial.id}`);
      setCurrentTheme(savedTheme ? JSON.parse(savedTheme) : memorialThemes.heritage);
    }
  }, [memorial?.id]);

  useEffect(() => {
    const qs       = new URLSearchParams(location.search);
    const tabParam = qs.get('tab');
    const valid    = ['story', 'showcase', 'tributes', 'gallery', 'events', 'familytree', 'settings', 'legacy'];
    if (tabParam && valid.includes(tabParam)) {
      const restricted = ['settings', 'legacy'];
      setActiveTab(restricted.includes(tabParam) && !isCreator ? 'story' : tabParam);
    } else {
      setActiveTab('story');
    }
    setIsUnlocked(!memorial.isPrivate || isCreator);
  }, [memorial.id, memorial.isPrivate, isCreator, location.search]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--theme-primary', currentTheme.primary);
    root.style.setProperty('--theme-accent',  currentTheme.accent);
    root.style.setProperty('--theme-bg',      currentTheme.background);
  }, [currentTheme]);

  const handleCandleClick = async () => {
    const nowLiked = !isLiked;
    setIsLiked(nowLiked);
    setCandleCount(c => nowLiked ? c + 1 : c - 1);
    if (nowLiked) {
      const session  = getSession();
      const userName = session?.name || session?.email || 'A visitor';
      writeFetch('create/candle_tributes', {
        method: 'POST',
        body: JSON.stringify({
          user_id: session?.id || 'guest', memorial_page_id: memorial.id,
          lit_by_name: userName, message: '', candle_type: 'gold',
          lit_at: new Date().toISOString(),
        }),
      }).catch(() => {});
      await notifyMemorialFollowers({
        memorialPageId: memorial.id, memorialName: memorial.name,
        actorName: userName, tributeType: 'candle', giftType: 'Eternal Gold',
      });
    }
  };

  if (!isUnlocked) {
    return <PrivateAccessLock memorial={memorial} onUnlock={() => setIsUnlocked(true)} />;
  }

  const tabs = [
    { id: 'story',      label: 'Story',         icon: FiBookOpen   },
    { id: 'showcase',   label: 'Memories',       icon: FiGrid       },
    { id: 'tributes',   label: 'Guestbook',      icon: FiHeart      },
    { id: 'gallery',    label: 'Gallery',         icon: FiImage      },
    { id: 'events',     label: 'Events',          icon: FiCalendar   },
    { id: 'familytree', label: 'Family Tree',     icon: FiGitBranch  },
    ...(isCreator ? [
      { id: 'settings', label: 'Customization',  icon: FiSettings   },
      { id: 'legacy',   label: 'Admin',           icon: FiShield     },
    ] : []),
  ];

  return (
    <div
      className={`min-h-screen pb-20 transition-colors duration-500 ${currentTheme.font}`}
      style={{ backgroundColor: 'var(--theme-bg)' }}
    >
      <SoundscapePlayer
        preferred={memorial.preferredSoundscape}
        memorialId={memorial.id}
      />

      {/* ── Hero Cover ── */}
      <div className="relative h-[40vh] md:h-[55vh] min-h-[300px]">
        <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
        <div
          className="absolute inset-0 transition-colors duration-500"
          style={{
            background: `linear-gradient(to top, var(--theme-primary) 0%, transparent 100%)`,
            opacity: 0.85,
          }}
        />

        {/* Back button */}
        <div className="absolute top-20 md:top-28 left-4 md:left-6 z-10">
          <Link
            to="/explore"
            className="text-white bg-black/20 backdrop-blur-md p-2 md:px-5 md:py-2.5 rounded-full flex items-center gap-2 text-sm font-bold border border-white/10 hover:bg-white/30 transition-all"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5 md:w-4 md:h-4" />
            <span className="hidden md:inline">Back to Explore</span>
          </Link>
        </div>

        {/* Profile + actions */}
        <div className="absolute -bottom-16 md:-bottom-20 left-0 w-full">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-center md:items-end gap-4 md:gap-8">
              {/* Avatar */}
              <div className="w-32 h-32 md:w-56 md:h-56 rounded-full border-4 border-white overflow-hidden shadow-2xl bg-white flex-shrink-0 z-10">
                <img src={profileImage} alt={memorial.name} className="w-full h-full object-cover" />
              </div>

              {/* Name & dates */}
              <div className="text-center md:text-left md:pb-6 z-10 flex-grow">
                <h1 className="text-3xl md:text-6xl text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.85)] mb-1 font-serif">
                  {memorial.name}
                </h1>
                <p className="font-bold tracking-widest text-sm md:text-xl" style={{ color: 'var(--theme-accent)' }}>
                  {new Date(memorial.birthDate).getFullYear()} — {new Date(memorial.deathDate).getFullYear()}
                </p>
              </div>

              {/* CTA buttons */}
              <div className="flex gap-3 md:pb-6 z-10 w-full md:w-auto px-4 md:px-0">
                <button
                  onClick={handleCandleClick}
                  className="flex-grow md:flex-none text-white px-6 py-3 rounded-full font-bold transition-all active:scale-95 shadow-md flex items-center justify-center gap-2"
                  style={{ backgroundColor: isLiked ? '#EF4444' : 'var(--theme-accent)' }}
                >
                  <span className="text-xl leading-none">🕯️</span>
                  {isLiked ? `Lit (${candleCount})` : `Light a Candle (${candleCount})`}
                </button>

                <button
                  onClick={() => setShareOpen(true)}
                  className="p-3 bg-white text-accent-navy rounded-full shadow-lg border border-brand-dark hover:bg-brand-light transition-colors"
                  title="Share this memorial"
                  aria-label="Share memorial"
                >
                  <SafeIcon icon={FiShare2} className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="container mx-auto px-4 md:px-6 mt-24 md:mt-32">
        {signedIn && !isCreator && (
          <ClaimMemorialBanner onClaim={claim} claiming={claiming} error={claimError} />
        )}
        <div className="flex flex-col lg:flex-row gap-10 md:gap-16">

          {/* Left — tabs + content */}
          <div className="lg:w-2/3">
            {/* Tab bar */}
            <div
              className="flex overflow-x-auto hide-scrollbar border-b border-brand-dark mb-8 sticky top-[68px] md:top-[80px] backdrop-blur-md z-30 -mx-4 px-4 md:mx-0 md:px-0"
              style={{ backgroundColor: `${currentTheme.background}F2` }}
            >
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex items-center gap-2 px-6 py-4 font-bold whitespace-nowrap transition-all relative"
                  style={{ color: activeTab === tab.id ? 'var(--theme-primary)' : '#6B7280' }}
                >
                  <SafeIcon
                    icon={tab.icon}
                    className="w-4 h-4"
                    style={{ color: activeTab === tab.id ? 'var(--theme-accent)' : 'inherit' }}
                  />
                  {tab.label}
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="memTabUnderline"
                      className="absolute bottom-0 left-0 right-0 h-1 rounded-t-full"
                      style={{ backgroundColor: 'var(--theme-accent)' }}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="min-h-[400px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  {activeTab === 'story'      && <TabStory      memorial={memorial} />}
                  {activeTab === 'showcase'   && <TabShowcase   memorial={memorial} />}
                  {activeTab === 'tributes'   && <TabTributes   memorial={memorial} />}
                  {activeTab === 'gallery'    && <TabGallery    memorial={memorial} />}
                  {activeTab === 'events'     && <TabEvents     memorial={memorial} />}
                  {activeTab === 'familytree' && <TabFamilyTree memorial={memorial} />}
                  {activeTab === 'legacy'     && <TabLegacySettings memorial={memorial} />}
                  {activeTab === 'settings'   && (
                    <div className="space-y-6">
                      <div className="flex gap-2 p-1.5 bg-brand-light rounded-2xl border border-brand-dark">
                        {CUSTOMIZATION_SUBTABS.map(st => (
                          <button
                            key={st.id}
                            onClick={() => setCustomSubTab(st.id)}
                            className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${
                              customSubTab === st.id
                                ? 'bg-white shadow-sm text-accent-navy border border-brand-dark'
                                : 'text-text-muted hover:text-accent-navy'
                            }`}
                          >
                            <SafeIcon icon={st.icon} className="w-3.5 h-3.5" />
                            {st.label}
                          </button>
                        ))}
                      </div>
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={customSubTab}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.2 }}
                        >
                          {customSubTab === 'theme'     && (
                             <ThemeSelector 
                                memorial={memorial} 
                                currentTheme={currentTheme} 
                                onThemeChange={setCurrentTheme} 
                                onSave={handleSaveTheme} 
                                coverImage={coverImage}
                                profileImage={profileImage}
                                setCoverImage={setCoverImage}
                                setProfileImage={setProfileImage}
                             />
                          )}
                          {customSubTab === 'platforms' && <MemorialPlatformSettings memorial={memorial} />}
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Right — sidebar widgets */}
          <div className="lg:w-1/3 space-y-6 md:space-y-8">
            <MilestoneCountdown milestones={memorial.milestones} name={memorial.name} />
            {memorial.donations && <DonationProgressWidget memorial={memorial} />}
            <EstateManagementWidget memorial={memorial} />
            {user && (
              <TributeNotificationSettings
                memorialPageId={memorial.id}
                memorialName={memorial.name}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Share Modal ── */}
      <ShareModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`In Loving Memory of ${memorial.name}`}
        url={`${window.location.origin}/#/memorial/${memorial.id}`}
        memorialId={memorial.id}
        memorialName={memorial.name}
        description={memorial.tagline || `A memorial page for ${memorial.name}`}
      />
    </div>
  );
};

export default MemorialDetail;